import { Box } from '@radix-ui/themes';
import { Layer as LayerLib } from 'konva/lib/Layer';
import { KonvaEventObject } from 'konva/lib/Node';
import { Stage as StageLib } from 'konva/lib/Stage';
import { Vector2d } from 'konva/lib/types';
import { useLayoutEffect, useRef, useState } from 'react';
import { Layer, Stage } from 'react-konva';
import { useStore } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { RootState } from '@/app/appStore';
import { BgImage } from '@/pages/home/ui/bg-image';
import { BgRectangle } from '@/pages/home/ui/bg-rectangle';
import { CrossHair } from '@/pages/home/ui/crosshair';
import { FrameImage } from '@/pages/home/ui/frame-image';
import { SkuImages } from '@/pages/home/ui/sku-images';
import { SkuSelect } from '@/pages/home/ui/sku-select';
import { VideoStream } from '@/pages/home/ui/video-stream';
import {
  DrawMode,
  drawBackground,
  getRelativePointerPosition,
  updateMousePosition,
  updateDimensions,
} from '@/entities/canvas';
import {
  attachPointToSku,
  availableSkus,
  selectIsControlsDisabled,
} from '@/entities/collection';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

export function Workspace() {
  const [videoKey, setVideoKey] = useState(0);
  const selectedFrameIndex = useAppSelector(
    (state) => state.collection.selectedFrameIndex
  );
  const canvasDimensions = useAppSelector((state) => state.canvas.dimensions);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const imageLayer = useRef<LayerLib | null>(null);
  const stage = useRef<StageLib | null>(null);
  const dispatch = useAppDispatch();
  const VIDEO_ASPECT_RATIO = 16 / 9;
  const drawMode = useAppSelector((state) => state.canvas.drawMode);
  const store = useStore<RootState>();
  const [selectedPoint, setSelectedPoint] = useState<Vector2d | null>(null);
  const isControlsDisabled = useAppSelector(selectIsControlsDisabled);
  const [isBlurVisible, setIsBlurVisible] = useState(false);

  useLayoutEffect(() => {
    const onResize = () => {
      if (!canvasRef.current) return;

      dispatch(
        updateDimensions({
          canvasWidth: canvasRef.current.clientWidth,
          canvasHeight: canvasRef.current.clientHeight,
        })
      );

      const imageHeight = canvasRef.current.clientWidth / VIDEO_ASPECT_RATIO;
      const layerOffsetY = imageHeight / 2 - canvasRef.current.clientHeight / 2;
      imageLayer.current?.offsetY(layerOffsetY);
    };

    onResize();

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const drawMouseDown = () => {
    if (!drawMode) return;

    const pointer = getRelativePointerPosition(imageLayer.current);

    if (!pointer) return;

    pointer.y = Math.floor(pointer.y);

    if (drawMode === DrawMode.Background) {
      if (
        pointer.y < 0 ||
        pointer.y > canvasDimensions.canvasWidth / VIDEO_ASPECT_RATIO
      )
        return;

      dispatch(drawBackground({ pointer }));
    }
  };

  const drawMouseMove = (pointer: Vector2d | undefined) => {
    if (!drawMode) return;
    if (!pointer) return;
  };

  const attachToSku = (e: KonvaEventObject<MouseEvent>) => {
    const state = store.getState();
    const point = getRelativePointerPosition(imageLayer.current);
    const availableSkuList = availableSkus({ collection: state.collection });

    if (!point || !availableSkuList.length) return;

    if (e.target.nodeType === 'Shape') {
      if (availableSkuList.length === 1) {
        dispatch(
          attachPointToSku({
            variantIndex: availableSkuList[0].variantIndex,
            point: {
              ...point,
              relativeX: point.x / canvasDimensions.canvasWidth,
              relativeY:
                (point.y * VIDEO_ASPECT_RATIO) / canvasDimensions.canvasWidth,
            },
          })
        );
        setSelectedPoint(null);
        return;
      }

      setSelectedPoint(point);
    } else if (selectedPoint) {
      setSelectedPoint(null);
    }
  };

  const onMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    if (isControlsDisabled) return;

    if (drawMode) {
      drawMouseDown();
    } else if (store.getState().collection.mode === 'multi') {
      attachToSku(e);
    }
  };

  const onMouseMove = () => {
    const pointer = getRelativePointerPosition(imageLayer.current);

    if (pointer) {
      pointer.y = Math.floor(pointer.y);
    }

    if (drawMode) {
      drawMouseMove(pointer);
    }

    if (pointer) {
      dispatch(
        updateMousePosition({
          x: pointer.x / canvasDimensions.canvasWidth,
          y: (pointer.y * VIDEO_ASPECT_RATIO) / canvasDimensions.canvasWidth,
        })
      );
    }
  };

  return (
    <div
      className="flex h-full relative"
      style={{
        cursor: drawMode === DrawMode.Background ? 'crosshair' : 'default',
      }}
    >
      <div className="flex flex-col relative z-20 flex-grow h-full">
        <Box
          ref={canvasRef}
          className={twMerge(['flex-grow relative'])}
          autoFocus
        >
          <Stage
            tabIndex={0}
            ref={stage}
            className={twMerge(['stage absolute top-0 left-0 outline-none'])}
            width={canvasDimensions.canvasWidth}
            height={canvasDimensions.canvasHeight}
            // onTouchStart={checkDeselect}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onContextMenu={(e) => {
              // disable context menu
              e.evt.preventDefault();
              return false;
            }}
          >
            <Layer ref={imageLayer}>
              <FrameImage
                width={canvasDimensions.canvasWidth}
                height={canvasDimensions.canvasWidth / VIDEO_ASPECT_RATIO}
              />
              <BgImage
                width={canvasDimensions.canvasWidth}
                height={canvasDimensions.canvasWidth / VIDEO_ASPECT_RATIO}
              />
              <BgRectangle />
              <SkuImages />
              {selectedPoint && (
                <SkuSelect
                  canvasWidth={canvasDimensions.canvasWidth}
                  target={selectedPoint}
                  onSelect={() => {
                    setSelectedPoint(null);
                  }}
                />
              )}
              <CrossHair stageSize={canvasDimensions} />
            </Layer>
          </Stage>
        </Box>
      </div>

      {selectedFrameIndex < 0 && (
        <VideoStream
          id="live-stream"
          key={videoKey}
          onRetry={() => setVideoKey(videoKey + 1)}
          isBlurVisible={isBlurVisible}
          onBlurVisibilityChange={setIsBlurVisible}
        />
      )}
    </div>
  );
}
